// Typed client for ViewRequisitionController (V2 region) + the v1 action endpoints the page reuses.
// HTTP goes through the portal HttpClient (Authorization, X-Portal-Key, Lab headers).
import {
  CountResult,
  PageResult,
  Row,
  StatusChangeRequest,
  TabConfig,
  WorklistRequest,
} from "../types";
import { normalizeTabs, pick, rowText } from "../utils";
import RequisitionType from "Services/Requisition/RequisitionTypeService";
import ViewRequisitionV2Service from "Services/Requisition/ViewRequisitionV2Service";

// Unwraps RequestResponse { data, message, status, httpStatusCode }; passes plain bodies through.
const unwrap = <T>(body: unknown): T =>
  (body && typeof body === "object" && pick(body, "data") !== undefined
    ? pick(body, "data")
    : body) as T;

// Full WorklistRequest body: every key present, empty values instead of null/missing.
const toRequestBody = (req: WorklistRequest) => ({
  tabId: req.tabId,
  pageSize: req.pageSize ?? 0,
  cursor: req.cursor ?? "",
  pageNumber: req.pageNumber ?? 1,
  sortColumn: req.sortColumn ?? "",
  sortDirection: req.sortDirection ?? "desc",
  filters: req.filters.map((f) => ({
    id: f.id,
    value: f.value ?? "",
    values: f.values ?? [],
    from: f.from ?? "",
    to: f.to ?? "",
    columnName: f.columnName ?? "",
    columnType: f.columnType ?? "",
    filterValue: f.filterValue ?? "",
  })),
});

const worklistApi = {
  // All tabs with columns (cached server-side 10 min, evicted by SaveColumnsV2).
  async getTabColumns(signal?: AbortSignal): Promise<TabConfig[]> {
    const res = await ViewRequisitionV2Service.getTabColumns({ signal });
    return normalizeTabs(unwrap(res.data));
  },

  // One page of rows. Pass nextCursor back as Cursor (default sort) or PageNumber+1 (custom sort).
  async page(req: WorklistRequest, signal?: AbortSignal): Promise<PageResult> {
    const res = await ViewRequisitionV2Service.getPage(toRequestBody(req), {
      signal,
    });
    return {
      rows: (pick<Row[]>(res.data, "rows") ?? []) as Row[],
      nextCursor: (pick<string>(res.data, "nextCursor") ?? null) as
        | string
        | null,
      hasMore: Boolean(pick(res.data, "hasMore")),
    };
  },

  // Capped total for tab + filters ("10,000+"). Cached server-side 30 s.
  async count(req: WorklistRequest, signal?: AbortSignal): Promise<CountResult> {
    const res = await ViewRequisitionV2Service.getCount(toRequestBody(req), {
      signal,
    });
    return {
      total: Number(pick(res.data, "total") ?? 0),
      isCapped: Boolean(pick(res.data, "isCapped")),
    };
  },

  // Badge counts keyed by TabId. Cached server-side 60 s; refresh=true bypasses it.
  async tabCounts(
    refresh = false,
    signal?: AbortSignal
  ): Promise<Record<number, number>> {
    const res = await ViewRequisitionV2Service.getTabCounts(refresh, {
      signal,
    });
    const out: Record<number, number> = {};
    Object.entries((res.data ?? {}) as Record<string, unknown>).forEach(
      ([k, v]) => (out[Number(k)] = Number(v))
    );
    return out;
  },

  // Drawer detail for one order. Returns null on 404 (not found / outside facility scope).
  async detail(
    requisitionOrderId: number,
    tabId: number,
    signal?: AbortSignal
  ): Promise<Row | null> {
    try {
      const res = await ViewRequisitionV2Service.getDetail(
        requisitionOrderId,
        tabId,
        { signal }
      );
      return res.data as Row;
    } catch (e: unknown) {
      if ((e as { response?: { status?: number } })?.response?.status === 404)
        return null;
      throw e;
    }
  },

  // Options for one dropdown/multiselect filter (raw JsonOptionData string).
  async filterOptions(
    tabId: number,
    columnId: number,
    signal?: AbortSignal
  ): Promise<string | null> {
    const res = await ViewRequisitionV2Service.getFilterOptions(
      tabId,
      columnId,
      { signal }
    );
    return (unwrap<string>(res.data) ?? null) as string | null;
  },

  /**
   * Saves the column layout for a tab. `columns` is the FULL list in display order;
   * the server stores the visible ones and numbers them 1..n in this order.
   */
  async saveColumns(
    tabId: number,
    columns: { id: number; isShow: boolean }[]
  ): Promise<void> {
    await ViewRequisitionV2Service.saveColumns({
      tabId,
      columns: columns.map((c) => ({ id: c.id, isShow: c.isShow })),
    });
  },

  // ---------------------------------------------------------- v1 endpoints reused by v2

  // StatusChangedV2 — Hold / Complete / Reject / Delete (same payload as v1 bulk actions).
  async changeStatus(req: StatusChangeRequest): Promise<void> {
    await RequisitionType.ViewRequisitionBulkStatusChange(req);
  },

  // NextStepAction — same FormData as v1 Row.handleSave.
  async nextStep(row: Row, file?: File): Promise<void> {
    const formData = new FormData();
    if (file) formData.append("file", file);
    formData.append("RequisitionId", rowText(row, "RequisitionId"));
    formData.append("NextStep", rowText(row, "NextStep"));
    formData.append(
      "RequisitionOrderId",
      rowText(row, "RequisitionOrderId", "__RowKey")
    );
    formData.append("RecordId", rowText(row, "RecordId"));
    formData.append("RequisitionType", rowText(row, "RequisitionTypeId"));
    await RequisitionType.NextStepAction(formData);
  },
};

export default worklistApi;
